import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { View } from 'react-native-web';

import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import Review from './Review';
import {
  HeaderText,
  BodyText,
  Stack,
  UnderlineLink,
  Button,
} from '../../../ui';
import StateLink from '../../../StateLink';
import { ReviewStarSummary } from '../ReviewStarSummary/ReviewStarSummary';
import ReviewEmptyState from './ReviewEmptyState';
import ReviewImageSummary from '../ReviewImageSummary';

import './ReviewList.module.scss';

export const propTypes = {
  /**
   * The name of the service that the reviews in this list pertain
   * to when showing reviews for a specific service
   */
  serviceName: PropTypes.string,
  /**
   * The list of reviews to display
   */
  reviews: PropTypes.array,
  /**
   * The total number of written reviews
   */
  reviewCount: PropTypes.number,
  /**
   * Is fetching more reviews from API
   */
  isLoading: PropTypes.bool,
  /**
   * When reviews are paginated, this indicates that there are more
   * reviews available to load
   */
  reviewsHaveNext: PropTypes.bool,
  /**
   * The Review List is used for a few different views. This boolean
   * indicates whether or not these reviews are on a page on their own
   * rather than being part of a larger view (like the profile)
   */
  standalonePage: PropTypes.bool,
  /**
   * This indicates that the page is in edit mode because the provider
   * that the reviews are about are viewing them.
   */
  isProvider: PropTypes.bool,
  /**
   * The provider the reviews are about,
   */
  provider: PropTypes.object.isRequired,
  /**
   * The id of the service that the reviews in this list pertain
   * to when showing reviews for a specific service
   */
  serviceId: PropTypes.number,
  /**
   * The total average star rating for the provider based on reviews
   */
  avgRating: PropTypes.number,
  /**
   * Callback for when there are more reviews available to load and the
   * user clicks the 'load more'
   */
  onShowMoreClick: PropTypes.func,
  /**
   * Callback to respond to or edit a response to a review when the
   * reviews are in edit mode
   */
  openResponseModal: PropTypes.func,
  /**
   * Callback to pin/unpin a review when in edit mode
   */
  onPinReview: PropTypes.func,
  /**
   * Function that generates the shareable facebook URL for a review
   * when in edit mode
   */
  getFacebookReviewShareUrl: PropTypes.func,
  /**
   * Analytics tracking callback for when a pro in edit mode shares the review
   */
  trackShare: PropTypes.func,
  /**
   * In edit mode and has no reviews
   */
  isEmpty: PropTypes.bool,
  /**
   * Called when a review image is pressed, passed the image
   */
  onReviewImageSelected: PropTypes.func,
  /**
   * ReviewSummaryPhotos, note: type defined in
   * types/ReviewTypes.ts::IReviewPhoto
   */
  reviewSummaryImages: PropTypes.object,
  /**
   * Called when an image is shown within the gallery.
   */
  onReviewImageShown: PropTypes.func,
  /**
   * Called when the review image all gallery is opened.
   */
  onReviewImageGalleryOpened: PropTypes.func,
  /** True to hide the star summary, image summary, header, and review count */
  hideSummary: PropTypes.bool,
  /** True to hide the pinned review at the top of the list */
  showPinnedReview: PropTypes.bool,

  /** Pinned review data passed from container */
  pinnedReview: PropTypes.object,
  /** Not to be confused with a pinned review, this is a review to display first in the list, and
   * may be duplicated in the list */
  highlightedReview: PropTypes.object,
  isInReviewTagFiltersTest: PropTypes.bool,
  isEndOfFilteredReviews: PropTypes.bool,
  showHeaderContent: PropTypes.bool,
};

const ReviewListBase = ({
  serviceName,
  reviews,
  serviceId,
  avgRating,
  onShowMoreClick,
  reviewsHaveNext,
  standalonePage,
  isProvider,
  reviewCount,
  isLoading,
  isEmpty,
  onReviewImageSelected,
  reviewSummaryImages,
  provider,
  onReviewImageShown,
  onReviewImageGalleryOpened,
  hideSummary,
  showPinnedReview,
  pinnedReview,
  highlightedReview,
  isInReviewTagFiltersTest,
  isEndOfFilteredReviews,
  showHeaderContent,
  ...rest
}) => {
  const { onPinReview } = rest;
  const providerId = provider.id;
  const displayedReviews = useMemo(() => {
    if (highlightedReview) {
      return [
        highlightedReview,
        ...reviews.filter(review => review.id !== highlightedReview.id),
      ];
    }

    return reviews;
  }, [
    highlightedReview,
    reviews,
  ]);

  return (
    <View styleName="container">
      {isEmpty && <ReviewEmptyState />}

      {!isEmpty && (
        <Stack space={8}>
          {!hideSummary && showHeaderContent && (
            <React.Fragment>
              {serviceName && (
                <HeaderText
                  styleName="headerText"
                  level="2"
                >
                  {serviceName} Review{reviews.length > 1 ? 's' : null}
                </HeaderText>
              )}
              {!isInReviewTagFiltersTest && (
                <ReviewStarSummary
                  standalonePage={standalonePage}
                  ratingsSummary={{
                    average_rating: avgRating,
                    total: provider.ratings_count,
                    stars: provider.ratings_count_by_stars,
                  }}
                />
              )}
              {!standalonePage && isProvider && (
                <Box justifyContent="center" alignItems="center" paddingY={8}>
                  <StateLink
                    to="responsive.provider.ratings_reviews"
                    params={{ providerId }}
                  >
                    <UnderlineLink>
                      View Star Ratings and Reviews
                    </UnderlineLink>
                  </StateLink>
                </Box>
              )}
              <ReviewImageSummary
                isInReviewTagFiltersTest={isInReviewTagFiltersTest}
                provider={provider}
                providerId={providerId}
                previewImages={reviewSummaryImages}
                onReviewImageShown={onReviewImageShown}
                onReviewImageGalleryOpened={onReviewImageGalleryOpened}
              />
            </React.Fragment>
          )}
          {pinnedReview ? (
            <Box paddingY={8} testID="pinnedreview__review">
              <Review
                {...rest}
                key={`pinned-${pinnedReview.id}`}
                isProvider={isProvider}
                provider={provider}
                {...pinnedReview}
                onReviewImageShown={onReviewImageShown}
                onReviewImageSelected={onReviewImageSelected}
                onUnpinReview={onPinReview}
                highlighted
                hideRespond
                hideTags
                hideImages
                isFirst
                isInReviewTagFiltersTest={isInReviewTagFiltersTest}
              />
            </Box>
          ) : null}
          {!pinnedReview && isProvider ? (
            <Box
              padding={16}
              borderColor={colors.lightGrey}
              borderWidth={1}
              borderStyle="solid"
              color={colors.whiteWash}
            >
              <Stack space={8}>
                <HeaderText
                  level={3}
                  textAlign="center"
                >
                  Highlight Review
                </HeaderText>
                <BodyText
                  textAlign="center"
                  testID="pinnedreviewempty__has-reviews-copy"
                >
                  Pin one of your reviews to your profile and it will show here.
                </BodyText>
              </Stack>
            </Box>
          ) : null}
          <Stack>
            {displayedReviews.map((review, index) => (
              <Review
                {...rest}
                key={review.id}
                isProvider={isProvider}
                provider={provider}
                {...review}
                reviewImages={review.review_images}
                onReviewImageShown={onReviewImageShown}
                onReviewImageSelected={onReviewImageSelected}
                onUnpinReview={onPinReview}
                isFirst={index === 0}
                isLast={index === reviews.length - 1}
                isInReviewTagFiltersTest={isInReviewTagFiltersTest}
              />
            ))}
          </Stack>
          {isLoading && <View className="loadingSpinner" />}
          {reviewsHaveNext && !isEndOfFilteredReviews && (
            <Button
              onClick={() => onShowMoreClick(providerId, serviceId)}
              appearance="link-accent-lowercase"
            >
              View More Reviews
            </Button>
          )}
        </Stack>
      )}
    </View>
  );
};

ReviewListBase.propTypes = propTypes;

ReviewListBase.defaultProps = {
  reviews: [],
  reviewCount: 0,
  serviceName: null,
  serviceId: null,
  avgRating: null,
  reviewsHaveNext: false,
  standalonePage: false,
  isProvider: false,
  onShowMoreClick: () => { },
  openResponseModal: undefined,
  onPinReview: undefined,
  getFacebookReviewShareUrl: undefined,
  trackShare: undefined,
  isLoading: false,
  isEmpty: false,
  onReviewImageSelected: null,
  reviewSummaryImages: {
    results: [],
  },
  onReviewImageShown: null,
  onReviewImageGalleryOpened: null,
  hideSummary: false,
  showPinnedReview: false,
  pinnedReview: null,
  highlightedReview: null,
  isInReviewTagFiltersTest: false,
  isEndOfFilteredReviews: false,
  showHeaderContent: true,
};

export default ReviewListBase;
